<script setup lang="ts">
/**
 * The one layout every form shares (#1756, FM10). The order is fixed, so no form can drift from it: the title, the
 * one-sentence purpose directly under it (outside the panel), the default fields, Advanced, then the action row —
 * right-aligned, Cancel to the left and the primary button last, bottom-right.
 *
 * The root is the `<form>` itself, so the attributes and the `submit` listener a screen puts on the layout reach the
 * form element. Advanced is the kit `Collapsible` with its count, closed on every load, and is left out entirely when
 * the form gives nothing for it. A screen's error line goes in `status` (under the purpose, above the fields), and the
 * save preview it opens in place goes in `preview`, under the action row.
 *
 * `heading` says how the title is drawn: `page` (the default) is the page's own title; `section` is for a form that
 * sits inside a larger page, such as a panel of the Agents or Tools screen, where the page already has its title.
 *
 * Advanced can also be opened by the screen (`v-model:advanced-open`), for a form whose own action fills fields under
 * Advanced and shows them (Create team's charter draft, #1759). Without it, Advanced is the user's alone, as before.
 * Either way `advanced-toggle` reports only the user's own opening or closing, never one the screen made.
 */

import { ref, watch } from 'vue'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible'
import ZhPageTitle from './ZhPageTitle.vue'
import ZhPanel from './ZhPanel.vue'
import ZhSectionHeading from './ZhSectionHeading.vue'

const props = withDefaults(
    defineProps<{
        title: string
        /** One sentence: what saving the form does. */
        purpose: string
        /** How many fields Advanced holds, shown on its header. */
        advancedCount?: number
        heading?: 'page' | 'section'
        /** A `data-testid` for the Advanced header, for a screen whose tests and tours already open it by one. */
        advancedTestid?: string
        /**
         * The whole form is one kit panel, title and purpose included, rather than its fields alone. For an edit form
         * that shares a create form's fields (editing a bound tool): edit panels sit outside the shared create layout
         * (#1756, CTO decision 1) and keep the single surface they always had.
         */
        framed?: boolean
        /**
         * The form draws no surface of its own: its fields and action row sit in a plain `div`, not a panel. For a
         * form that already sits on a surface (the Agents connection editor's panel), so it never nests a panel
         * inside one. The order of the parts is unchanged. This covers the layout's own parts only: a `ZhSavePreview`
         * in the `preview` slot draws its own panel unless it is given `bare` too.
         */
        bare?: boolean
        /** Whether Advanced is open, for a screen that opens it itself (`v-model:advanced-open`). Closed on load. */
        advancedOpen?: boolean
    }>(),
    {
        heading: 'page',
        advancedCount: undefined,
        advancedTestid: undefined,
        framed: false,
        bare: false,
        advancedOpen: false,
    },
)

const emit = defineEmits<{
    /** The user opened (true) or closed (false) Advanced. */
    'advanced-toggle': [open: boolean]
    /** Advanced's open state after the user's toggle, for `v-model:advanced-open`. */
    'update:advancedOpen': [open: boolean]
}>()

/** Advanced's open state: the screen's when it sets one, otherwise the user's toggles alone. */
const open = ref(props.advancedOpen)
watch(
    () => props.advancedOpen,
    value => {
        open.value = value
    },
)

function userToggled(value: boolean): void {
    open.value = value
    emit('update:advancedOpen', value)
    emit('advanced-toggle', value)
}

defineSlots<{
    default(): unknown
    status?(): unknown
    advanced?(): unknown
    cancel?(): unknown
    primary?(): unknown
    preview?(): unknown
}>()
</script>

<template>
    <component
        :is="framed ? ZhPanel : 'form'"
        v-bind="framed ? { as: 'form', measure: 'prose' } : {}"
        data-slot="form-layout"
        class="flex w-full flex-col gap-4"
    >
        <ZhPageTitle v-if="heading === 'page'" data-slot="form-layout-title">{{ title }}</ZhPageTitle>
        <ZhSectionHeading v-else data-slot="form-layout-title" as="h2">{{ title }}</ZhSectionHeading>
        <p data-slot="form-layout-purpose" class="text-muted-foreground text-sm">{{ purpose }}</p>
        <slot name="status" />
        <component
            :is="framed || bare ? 'div' : ZhPanel"
            v-bind="framed || bare ? {} : { measure: 'prose' }"
            class="space-y-3"
            data-slot="form-layout-fields"
        >
            <slot />
            <Collapsible v-if="$slots.advanced" data-slot="form-layout-advanced" :open="open" @update:open="userToggled">
                <CollapsibleTrigger :count="advancedCount" :data-testid="advancedTestid">Advanced</CollapsibleTrigger>
                <CollapsibleContent class="space-y-3 pt-2">
                    <slot name="advanced" />
                </CollapsibleContent>
            </Collapsible>
            <div data-slot="form-layout-actions" class="flex flex-wrap items-center justify-end gap-2">
                <slot name="cancel" />
                <slot name="primary" />
            </div>
        </component>
        <slot name="preview" />
    </component>
</template>
